import { assign, assignWith, isNull, isUndefined } from 'lodash';
/* eslint-disable */
/**
 * Assign With Defined
 *
 * Same as lodash assignWith except it has a customizer
 * that omits undefined or null values
 * @example
 *  assignWithDefined({d: 'Sunday', y: 2015}, {d: undefined, y: 2016}, {y: null})
 *  // → {d: 'Sunday', y: 2016}
 * @param args
 * @returns {any}
 */
function assignWithDefined(...args) {
  /**
   * Omit Empty
   * @param a {any} old value
   * @param b {any} new value
   */
  const omitEmpty = (a, b) => (!isUndefined(b) && !isNull(b) ? b : a);

  return assignWith.apply(null, [...args, omitEmpty]);
}

function collectionKeys(coll) {
  return (coll || []).reduce((accu, item) => {
    const inArray = (key) => accu.indexOf(key) < 0;
    return accu.concat(Object.keys(item).filter(inArray));
  }, []);
}

/**
 * Data Object To Collection Mixin
 *
 * Adds export functionality to "model" objects
 *
 * @example
 *    model = {
 *      data: {
 *        rows: ()=> ['1', '0'],
 *        records: [
 *          {id: 1, name: 'one'}
 *          {id: 2, name: 'two'}
 *        ]
 *      };
 *    };
 *    Object.assign(model, dataObjectToCollectionMixin(model.data));
 *    model.toCollection();
 *    -> [{id: 2, name: 'two'}, {id: 1, name: 'one'}]
 *
 * @todo make more generic
 * @todo apply a interface or type for parameters
 * @todo add support for non numeric row keys
 * @todo stop using data.records and consider using data.tree instead
 *  - if we go this way consider applying value type casting and formatting to values
 * @todo think about moving into straight model if other models will extend it
 * @param dataObj
 * @returns {{toCollection: (function(): Array)}}
 */
function dataObjectToCollectionMixin(dataObj) {
  return {
    /**
     * To Collection
     * @returns {any}
     */
    toCollection: function toCollection() {
      const records: Array<any> = dataObj.records;
      const rows: Array<string> = dataObj.rows();

      // generate a collection of records from sorted array of rowKeys
      return rows.reduce((accu, rowKey) => accu.push(records[rowKey]) && accu, []);
    },
  };
}

/**
 * Element Data
 *
 * Retrieve DOM element axel namespaced data attribute values
 * @example
 *
 *  # DOM
 *  <html><div id="my-element" data-axel-awesome-sauce="My Favorite"></div></html>
 *
 *  # Console
 *  el = document.getElementByID('my-element');
 *  elementData(el, 'awesome-sauce');
 *  // 'My Favorite'
 *  elementData(el, 'awesome-sauce', 'No Mine');
 *  elementData(el, 'awesome-sauce');
 *  // 'No Mine'
 *
 *  # jQuery Support
 *  elementData(jQuery('#my-element'), 'awesome-sauce');
 *  // 'No Mine'
 *
 * @param el
 * @param key
 * @param val
 */
function elementData(el, key, val = null) {
  const ns = 'axel';
  const dashKey = (k: string): string => [ns, k].join('-').toLowerCase();
  const camelKey = (k: string): string => dashKey(k).replace(/-([a-z])/g, (str) => str[1].toUpperCase());
  const isJquery = !!el.jquery;
  const get = (k) => (isJquery ? el.data(camelKey(k)) : el.getAttribute('data-' + dashKey(k)));
  const set = (k, v) => (isJquery ? el.data(camelKey(k), v) : el.setAttribute('data-' + dashKey(k), v));

  return val ? set(key, val) : get(key);
}

/**
 * Number Format Template
 *
 * Generate d3 number format template
 * @param opts
 * @returns {string}
 */
function numbersFormatTemplate(opts = {}) {
  const defaults = {
    fillValue: '',
    fillAlignment: '',
    sign: '',
    symbol: false,
    zeroPad: false,
    width: '',
    precision: 0,
    thousands: '',
    type: '',
  };
  const s: any = assign({}, defaults, opts);

  return [
    s.fillValue >= 0 && s.fillAlignment && s.width ? s.fillValue + s.fillAlignment : '',
    s.sign,
    s.symbol ? '$' : '',
    s.zeroPad ? 0 : '',
    s.width,
    s.thousands,
    s.type ? '.' + s.precision : '',
    s.type,
  ].join('');
}

function orderedComparatorMixin(comparatorA, comparatorB, order: 'asc' | 'desc' = 'asc') {
  return function (a, b) {
    return comparatorA.apply(null, /asc/.test(order) ? [a, b] : [b, a]) || comparatorB(a, b);
  };
}

/**
 * Median (Math)
 * @inspiredby https://gist.github.com/caseyjustus/1166258#gistcomment-1881872
 * @todo add unit tests
 * @param arr
 * @returns {any}
 */
function median(arr) {
  if (!arr.length) {
    return 0;
  }
  const numbers = arr.slice(0).sort((a, b) => a - b);
  const middle = Math.floor(numbers.length / 2);
  const isEven = numbers.length % 2 === 0;
  return isEven ? (numbers[middle] + numbers[middle - 1]) / 2 : numbers[middle];
}

function getTextWidth(
  text: string,
  fontName: string,
  fontSize: number,
  canvasElem = window.document.createElement('canvas')
) {
  const cxt = canvasElem.getContext('2d');

  cxt.font = `${fontSize}px ${fontName}`;
  return cxt.measureText(text).width;
}

export {
  median,
  assignWithDefined,
  collectionKeys,
  dataObjectToCollectionMixin,
  elementData,
  numbersFormatTemplate,
  orderedComparatorMixin,
  getTextWidth,
};
